import { useBoolean } from '@sravni/react-utils';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { checkIsShowAccordionBlockEditIcon } from 'shared/lib';
import { useAppSelector } from 'shared/lib/redux';
import type { IFormHeader } from 'shared/types/IFormHeader';
import { AnketaFormPopup } from 'shared/ui/AnketaFormPopup';
import { CustomAccordionItem } from 'shared/ui/CustomAccordion';

import { selectCreditPurposeInfo } from 'src/client/entities/creditForm';

import { CreditPurposeInfoModalTexts } from '../../CreditPurposeInfo.texts';
import { useAccordionHeaderParams } from '../../lib/useAccordionHeaderParams';
import { Form } from '../Form/Form';

export type TCreditPurposeAccordionProps = TAccordionProps & {
  shouldRestoreAdditionalData?: boolean;
};

export const AccordionModalForm: FC<TCreditPurposeAccordionProps> = ({
  isOpen,
  shouldForceOpenPopup = false,
  isFormForceOpened = false,
  onFormSubmit,
  isLoading,
  shouldRestoreAdditionalData,
  isDisableEdit = false,
  shouldScrollIntoAccordion,
  isPopupClosable = false,
  onPopupClose,
  children,
  isSummary,
  stepIndex,
  formId,
  icon,
  submitButtonText,
  isHideDivider = false,
  accordionIconSize,
  TitleComponent,
  DescriptionComponent,
  defaultDescription,
  defaultTitle,
  isAlwaysShowDescription = false,
  onPopupOpen,
  isAnyFormStepOpenedInPopup,
}) => {
  const { data, isFullFilled } = useAppSelector(selectCreditPurposeInfo);
  const [shouldShowEditPopup, setShouldShowEditPopup] = useBoolean();
  const [_, setHeader] = useState<IFormHeader>();
  const [title, description] = useAccordionHeaderParams(
    data,
    {
      title: defaultTitle,
      description: defaultDescription,
    },
    isOpen,
  );

  const isWithEditItem = checkIsShowAccordionBlockEditIcon(
    isOpen,
    isSummary || isFullFilled,
    isDisableEdit,
    shouldForceOpenPopup,
  );

  const isAccordionOpen = !isFullFilled && isOpen;
  const isEditPopupVisible = shouldShowEditPopup || (shouldForceOpenPopup && isFormForceOpened);
  // Если shouldForceOpenPopup false, то нужно давать возможнсть отредактировать данные и не дизейблить их
  // сделано для логики короткого продления, когда при восстановлении нет валидации
  const isDisabled = !isOpen && !isFullFilled && (shouldForceOpenPopup || isDisableEdit);
  const descriptionText = useMemo(
    () => (!isDisabled || isAlwaysShowDescription ? description : ''),
    [description, isAlwaysShowDescription, isDisabled],
  );
  const isWithDivider = !isSummary && !isHideDivider;

  const onEditButtonClick = useCallback(() => {
    onPopupOpen?.();
    setShouldShowEditPopup.on();
  }, [onPopupOpen, setShouldShowEditPopup]);

  return (
    <>
      <CustomAccordionItem
        onEditButtonClick={onEditButtonClick}
        icon={icon}
        iconSize={accordionIconSize}
        isOpen={isAccordionOpen}
        disabled={isDisabled}
        withEdit={isWithEditItem}
        title={TitleComponent ? <TitleComponent disabled={isDisabled}>{title}</TitleComponent> : title}
        withDivider={isWithDivider}
        description={
          DescriptionComponent ? (
            <DescriptionComponent disabled={isDisabled}>{descriptionText}</DescriptionComponent>
          ) : (
            descriptionText
          )
        }
        scrollIntoView={isAccordionOpen && shouldScrollIntoAccordion}
      >
        <Form
          onFormSubmit={onFormSubmit}
          setHeader={setHeader}
          isFormForceOpened={isFormForceOpened}
          isLoading={isLoading}
          shouldRestoreAdditionalData={shouldRestoreAdditionalData}
          stepIndex={stepIndex}
          formId={formId}
          submitButtonText={submitButtonText}
          isAnyFormStepOpenedInPopup={isAnyFormStepOpenedInPopup}
        />
      </CustomAccordionItem>

      <AnketaFormPopup
        submitButtonText={submitButtonText}
        isClosable={isPopupClosable}
        isFormForceOpened
        FormElement={Form}
        onFormSubmit={onFormSubmit}
        onClose={(isSubmit) => {
          setShouldShowEditPopup.off();
          onPopupClose?.(isSubmit);
        }}
        isLoading={isLoading}
        isOpen={isEditPopupVisible}
        icon={icon}
        iconSize={accordionIconSize}
        title={TitleComponent ? <TitleComponent>{defaultTitle}</TitleComponent> : defaultTitle}
        subtitle={
          DescriptionComponent ? (
            <DescriptionComponent>{CreditPurposeInfoModalTexts.description}</DescriptionComponent>
          ) : (
            CreditPurposeInfoModalTexts.description
          )
        }
        setHeader={setHeader}
        stepIndex={stepIndex}
      >
        {children}
      </AnketaFormPopup>
    </>
  );
};
